<template>
  <div v-if="!isSavedAsset">
    <CurrencyFormSearch
      :instance
      :disabled="loading"
      data-refid="currencyFormAvailableCurrencies"
      @select="handleSelect"
      @ready="e => e.focus()"
    />
  </div>
  <template v-else>
    <component
      :is="instance.getFormName()"
      v-bind="{
        dataRefid: 'currencyFormName',
        focusOnLoad: focusNameOnLoad,
        instance,
        label: ASSET_FIELD_LABEL[ASSET_FIELD.NAME],
        readonly,
      }"
    />
    <component
      :is="instance.getFormTicker()"
      v-bind="{
        dataRefid: 'currencyFormTicker',
        focusOnLoad: focusTickerOnLoad,
        instance,
        label: ASSET_FIELD_LABEL[ASSET_FIELD.TICKER],
      }"
      :readonly="readonly || isSavedAsset"
    />
  </template>
  <UIButtonsGroup>
    <template #default="{ short }">
      <UIButtonClose :short @click="handleClose" />
      <UIButtonSave
        v-if="!readonly"
        ref="buttonSaveRef"
        data-refid="currencyFormSave"
        :disabled
        :loading
        :short
        @click="handleSave"
      />
    </template>
  </UIButtonsGroup>
</template>

<script setup lang="ts">
import {
  computed,
  inject,
  markRaw,
  nextTick,
  onMounted,
  provide,
  ref,
  useTemplateRef
} from 'vue'

import { ReadonlyMode } from '@types'
import { AssetClass } from '..'
import { EntityState } from '@/entities/utils/enums'

import { ASSET_FIELD, ASSET_FIELD_LABEL, ASSET_ID_PREFIX } from '../utils/const'
import { ROUTE_NAME } from '@/const'
import { NOTIFICATION_DELAY, READONLY_MODE } from '@/const/common'

import { handleCatchedError } from '@/helpers/common'
import { SoftDeletedError } from '@/store/utils/errors'

import { useModalsStore } from '@/store/modals'
import { NotificationAction, useNotifications } from '@/plugins/notification'

import { UIButtonClose, UIButtonSave, UIButtonsGroup } from '@ui/buttons'
import CurrenciesTrashPopup from '@/views/Data/Currencies/components/CurrenciesTrashPopup.vue'
import CurrencyFormSearch from './CurrencyFormSearch.vue'
import { AxiosError } from 'axios'

type Props = {
  instance: AssetClass
}

type Emits = {
  close: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const modalsStore = useModalsStore()
const { error, progress, remove, update } = useNotifications()

const loading = ref(false)
const currency = ref<string>()

const buttonSaveRef = useTemplateRef('buttonSaveRef')
provide('asset-save-focus', async () => {
  await nextTick()
  buttonSaveRef.value?.focus()
})

const readonly = inject<ReadonlyMode>(READONLY_MODE)


const disabled = computed(
  () => ![EntityState.CHANGED, EntityState.NEW].includes(props.instance.state),
)

const isSavedAsset = computed(() =>
  props.instance.id.startsWith(ASSET_ID_PREFIX),
)
provide('isSavedAsset', isSavedAsset)

const focusNameOnLoad = computed(
  () => !isSavedAsset.value && !props.instance.field(ASSET_FIELD.NAME).value,
)
const focusTickerOnLoad = computed(
  () =>
    !focusNameOnLoad.value && !props.instance.field(ASSET_FIELD.TICKER).value,
)

const openDeletedModal = () => {
  const modalInstance = modalsStore.init(
    'deleted-currencies',
    markRaw(CurrenciesTrashPopup),
  )
  modalInstance?.open(modalsStore.getZIndex())
}

const handleClose = () => {
  emit('close')
}

const handleSave = async () => {
  loading.value = true
  const isNew = props.instance.isNew
  const nid = await progress({
    message: `${isNew ? 'Creating' : 'Updating'} currency`,
  })
  try {
    if (isNew) {
      await props.instance.store()
    } else {
      await props.instance.update()
    }
    await update(
      nid,
      {
        type: 'success',
        message: `Currency ${props.instance.field(ASSET_FIELD.NAME).value} ${
          isNew ? 'created' : 'updated'
        }`,
        link: {
          text: 'Open',
          to: {
            name: ROUTE_NAME.CURRENCIES_ITEM,
            params: { id: props.instance.id },
          },
        },
      },
      NOTIFICATION_DELAY,
    )
    emit('close')
  } catch (e) {
    await remove(nid)
    if (e instanceof SoftDeletedError) {
      const actions: NotificationAction[] = [
        {
          label: 'Go to deleted currencies',
          buttonVariant: 'primary',
          onClick: remove => {
            remove && remove()
            openDeletedModal()
          },
        },
        {
          label: 'Close',
          buttonVariant: 'gray',
          buttonFill: 'outlined',
          onClick: remove => {
            remove && remove()
          },
        },
      ]

      await error({ message: e.message, actions })
      return
    } else {
      const err = e as AxiosError
      error({ message: err.message })
    }
    handleCatchedError(e as string, props.instance.get())
  } finally {
    loading.value = false
  }
}

const handleSelect = (label: string, ticker: string) => {
  currency.value = ticker
  props.instance.set({
    name: label,
    ticker: ticker,
  })
}

onMounted(() => {
  if (isSavedAsset.value) return
  const ticker = props.instance.field<string>(ASSET_FIELD.TICKER).value
  if (ticker) {
    currency.value = ticker
  }
})
</script>
